import { useCallback, useMemo, useState } from "react";
import React from "react";
import { graphql, useFragment } from "react-relay";
import type { CSSObjectWithLabel } from "react-select";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faPen } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { Avatar, Button } from "~/ui";
import { Input } from "~/ui/legacyFellowIndex";
import Selector from "~/ui/selectors/Selector";

import { type RuleDescriptors, useAutoRecordRuleDescriptors } from "../AutoRecordRulesListItem";
import { useCreateAndEditModal } from "./CreateAndEdit";
import type { UserInput } from "./types";

import type { CreateAndEditModalAccount$data } from "~/__generated__/CreateAndEditModalAccount.graphql";
import "~/__generated__/CreateAndEditModalCreateRuleMutation.graphql";
import type { AutoRecordRuleFilterOrganizer } from "~/__generated__/CreateAndEditModalCreateRuleMutation.graphql";
import type { FilterAttendeeType } from "~/__generated__/CreateAndEditModalUpdateRuleMutation.graphql";
import type { RuleConfigurationStepUserPill$key } from "~/__generated__/RuleConfigurationStepUserPill.graphql";

import styles from "./RuleConfigurationStep.module.css";

enum AttendeeCountType {
	ANY = "0",
	LESS_THAN = "1",
	MORE_THAN = "2",
	IN_BETWEEN = "3",
}

interface UserPillProps {
	userInput: UserInput;
}

function UserPill({ userInput }: UserPillProps) {
	const fragment = graphql`
		fragment RuleConfigurationStepUserPill on User {
			id
			fullName
			image
			isGuest
			email
		}
	`;

	const user = useFragment<RuleConfigurationStepUserPill$key>(fragment, userInput?.user ?? null);

	if (!user || !userInput) {
		return null;
	}

	return (
		<div className={styles.userPill}>
			<Avatar user={user} size="small" />
			<span>
				{user.fullName}
				{userInput.includeAllReports && <> + all reports</>}
			</span>
		</div>
	);
}

function WorkspacePill({ account }: { account: CreateAndEditModalAccount$data }) {
	return (
		<div className={styles.userPill}>
			<Avatar
				user={{
					image: account.logo,
					fullName: account.name,
					isGuest: false,
				}}
				size="small"
			/>
			<span>{account.name}</span>
		</div>
	);
}

const getAttendeeCountType = (
	minAttendees: number | undefined,
	maxAttendees: number | undefined,
): AttendeeCountType => {
	if (!minAttendees && !maxAttendees) {
		return AttendeeCountType.ANY;
	} else if (!minAttendees) {
		return AttendeeCountType.LESS_THAN;
	} else if (!maxAttendees) {
		return AttendeeCountType.MORE_THAN;
	}
	return AttendeeCountType.IN_BETWEEN;
};

function NumberOfAttendeesInputs({ descriptors }: { descriptors: RuleDescriptors }) {
	const context = useCreateAndEditModal();

	const [filterAttendeeCountType, setFilterAttendeeCountType] = useState(
		getAttendeeCountType(context.rule.filterMinimumAttendees, context.rule.filterMaximumAttendees),
	);

	const filterAttendeeCountOptions = [
		{
			label: "Any number",
			value: AttendeeCountType.ANY,
		},
		{
			label: "less than or equal to",
			value: AttendeeCountType.LESS_THAN,
		},
		{
			label: "more than or equal to",
			value: AttendeeCountType.MORE_THAN,
		},
		{
			label: "between",
			value: AttendeeCountType.IN_BETWEEN,
		},
	];

	const setAttendeeCountType = useCallback(
		(
			option: {
				label: string;
				value: string;
			} | null,
		) => {
			if (!option) {
				return;
			}

			switch (option.value) {
				case AttendeeCountType.ANY:
					context.setFilterMinimumAttendees(undefined);
					context.setFilterMaximumAttendees(undefined);
					break;
				case AttendeeCountType.LESS_THAN:
					context.setFilterMinimumAttendees(undefined);
					context.setFilterMaximumAttendees(5);
					break;
				case AttendeeCountType.MORE_THAN:
					context.setFilterMaximumAttendees(undefined);
					context.setFilterMinimumAttendees(2);
					break;
				case AttendeeCountType.IN_BETWEEN:
					context.setFilterMinimumAttendees(3);
					context.setFilterMaximumAttendees(5);
					break;
			}

			setFilterAttendeeCountType(option.value as AttendeeCountType);
		},
		[context],
	);

	const updateFilterMinimumAttendees = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			context.setFilterMinimumAttendees(parseInt(event.target.value, 10));
		},
		[context],
	);

	const updateFilterMaximumAttendees = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			context.setFilterMaximumAttendees(parseInt(event.target.value, 10));
		},
		[context],
	);

	const inputs = useMemo(() => {
		switch (filterAttendeeCountType) {
			case AttendeeCountType.LESS_THAN:
				return (
					<>
						<Input
							className={styles.settingInput}
							value={context.rule.filterMaximumAttendees}
							onChange={updateFilterMaximumAttendees}
							type="number"
							min={2}
							name="filter_maximum_attendees"
						/>
					</>
				);
			case AttendeeCountType.MORE_THAN:
				return (
					<>
						<Input
							className={styles.settingInput}
							value={context.rule.filterMinimumAttendees}
							onChange={updateFilterMinimumAttendees}
							type="number"
							min={0}
							name="filter_minimum_attendees"
						/>
					</>
				);
			case AttendeeCountType.IN_BETWEEN:
				return (
					<>
						<Input
							className={styles.settingInput}
							value={context.rule.filterMinimumAttendees}
							onChange={updateFilterMinimumAttendees}
							type="number"
							min={0}
							max={(context.rule.filterMaximumAttendees || 1) - 1}
							name="filter_min_attendees_range"
						/>
						<span>and</span>
						<Input
							className={styles.settingInput}
							value={context.rule.filterMaximumAttendees}
							onChange={updateFilterMaximumAttendees}
							type="number"
							min={(context.rule.filterMinimumAttendees || 1) + 1}
							name="filter_max_attendees_range"
						/>
					</>
				);
			default:
				return null;
		}
	}, [
		context.rule.filterMaximumAttendees,
		context.rule.filterMinimumAttendees,
		filterAttendeeCountType,
		updateFilterMaximumAttendees,
		updateFilterMinimumAttendees,
	]);

	return (
		<div className={styles.formListItem}>
			<div className={styles.selectorRow}>
				<label>Number of invitees is</label>

				<div className={styles.inputs}>
					<Selector
						condensed
						onChange={setAttendeeCountType}
						options={filterAttendeeCountOptions}
						value={filterAttendeeCountOptions.find(({ value }) => value === filterAttendeeCountType)}
						styles={{
							singleValue: (base: CSSObjectWithLabel) => {
								return {
									...base,
									fontWeight: 400,
									color: "var(--primaryTextColor)",
								};
							},
						}}
					/>
					{inputs}
				</div>
			</div>
			{descriptors.attendeesDescriptor && <p>{descriptors.attendeesDescriptor}</p>}
		</div>
	);
}

interface KeywordsInputProps {
	placeholder: string;
	setKeywords: React.Dispatch<React.SetStateAction<string[]>>;
	keywords: string[];
}

function KeywordsPill({ keyword, onRemove }: { keyword: string; onRemove: () => void }) {
	const context = useCreateAndEditModal();

	const allKeywords = context.rule.filterKeywordsContains.concat(context.rule.filterKeywordsRestrict);
	const isDuplicate = allKeywords.filter(kw => kw === keyword).length > 1;

	return (
		<div className={cx(styles.keyword, { [styles.duplicateKeyword]: isDuplicate })} onClick={onRemove}>
			<div className={styles.keywordText}>{keyword}</div>
			<FontAwesomeIcon icon={faTimes} size="sm" />
		</div>
	);
}

function KeywordsInput({ placeholder, keywords, setKeywords }: KeywordsInputProps) {
	const [inputValue, setInputValue] = useState("");

	const addKeyword = useCallback(() => {
		const newKeywords = inputValue
			.split(",")
			.map(kw => kw.trim())
			.filter(kw => kw.length > 0);
		if (newKeywords.length > 0) {
			setKeywords((keywords: string[]) => {
				return [...keywords, ...newKeywords];
			});
			setInputValue("");
		}
	}, [inputValue, setKeywords]);

	const updateInputValue = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(event.target.value);
	}, []);

	const onInputKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Enter" || event.key === ",") {
				addKeyword();
				event.preventDefault();
			}
		},
		[addKeyword],
	);

	const onRemoveKeyword = useCallback(
		(index: number) => {
			setKeywords(keywords => {
				return keywords.filter((_, i) => i !== index);
			});
		},
		[setKeywords],
	);

	const getOnRemoveForIndex = (index: number) => {
		return () => onRemoveKeyword(index);
	};

	return (
		<div className={styles.keywordsInputContainer}>
			<div className={styles.keywordsInputRow}>
				<Input
					placeholder={placeholder}
					onChange={updateInputValue}
					value={inputValue}
					onKeyDown={onInputKeyDown}
					className={styles.keywordsInput}
					name="filter_keywords"
				/>
				<Button variant="primary" onClick={addKeyword} size="slim" disabled={inputValue.length === 0}>
					Add
				</Button>
			</div>
			<div className={styles.keywordsList}>
				{keywords.map((keyword, index) => {
					// eslint-disable-next-line react/no-array-index-key
					return <KeywordsPill key={`${keyword}-${index}`} keyword={keyword} onRemove={getOnRemoveForIndex(index)} />;
				})}
			</div>
		</div>
	);
}

const filterAttendeeTypeOptions: { label: string; value: FilterAttendeeType }[] = [
	{
		label: "Internal and External",
		value: "INTERNAL_AND_EXTERNAL",
	},
	{
		label: "Internal only",
		value: "INTERNAL_ONLY",
	},
	{
		label: "External only",
		value: "EXTERNAL_ONLY",
	},
];

const filterOrganizerOptions: { label: string; value: AutoRecordRuleFilterOrganizer }[] = [
	{
		label: "Anyone",
		value: "ANYONE",
	},
	{
		label: "Me",
		value: "SELF",
	},
];

export function RuleConfigurationStep() {
	const context = useCreateAndEditModal();

	const workspace = context.rule.ruleLevel === "WORKSPACE" ? context.account : undefined;

	const updateFilterAttendeeType = useCallback(
		(option: { label: string; value: FilterAttendeeType } | null) => {
			if (option) {
				context.setAttendeeType(option.value);
			}
		},
		[context],
	);

	const [containsSubtext, doesNotContainSubtext] = useMemo(() => {
		if (context.rule.ruleType === "RECORD") {
			return [
				// eslint-disable-next-line max-len
				"Meeting will be auto-recorded if event title or invitees name, email or domain match any of the following keywords:",
				// eslint-disable-next-line max-len
				"Meeting will be auto-recorded if event title or invitees name, email or domain do not match any of the following keywords:",
			];
		} else {
			return [
				// eslint-disable-next-line max-len
				"Meeting will be blocked from being recorded if event title or invitees name, email or domain match any of the following keywords:",
				// eslint-disable-next-line max-len
				"Meeting will be blocked from being recorded if event title or invitees name, email or domain do not match any of the following keywords:",
			];
		}
	}, [context.rule.ruleType]);

	const descriptors = useAutoRecordRuleDescriptors(context.rule, context.account);

	const updateFilterOrganizer = useCallback(
		(option: { label: string; value: AutoRecordRuleFilterOrganizer } | null) => {
			if (option) {
				context.setFilterOrganizer(option.value);
			}
		},
		[context],
	);

	let formHeading = undefined;
	if (context.rule.ruleLevel === "USER") {
		formHeading = "Automatically record the meetings I'm invited to, where:";
	} else if (context.rule.ruleType === "RECORD") {
		formHeading = "Automatically record meetings where:";
	} else {
		formHeading = "Block recording on meetings where:";
	}

	return (
		<div className={styles.ruleConfigurationBody}>
			{context.rule.ruleLevel !== "USER" && (
				<div className={styles.previousStepContainer}>
					<div className={styles.usersListContainer}>
						<div className={styles.usersList}>
							{workspace && <WorkspacePill account={workspace} />}
							{context.rule.autoRecordRuleUsers
								.map((userInput, index) => {
									if (userInput) {
										// eslint-disable-next-line react/no-array-index-key
										return <UserPill userInput={userInput} key={`${userInput.user?.id}-${index}`} />;
									}
									return null;
								})
								.filter(Boolean)}
						</div>
					</div>
					<div className={styles.editIconContainer}>
						<FontAwesomeIcon icon={faPen} className={styles.editIcon} onClick={context.previousStep} />
					</div>
				</div>
			)}
			<div className={styles.formContainer}>
				<label>{formHeading}</label>

				{context.rule.ruleLevel === "USER" && (
					<div className={styles.formListItem}>
						<div className={styles.selectorRow}>
							<label>Organizer is</label>
							<div className={styles.inputs}>
								<Selector
									condensed
									onChange={updateFilterOrganizer}
									options={filterOrganizerOptions}
									value={filterOrganizerOptions.find(option => option.value === context.rule.filterOrganizer)}
									styles={{
										singleValue: (base: CSSObjectWithLabel) => ({
											...base,
											fontWeight: 400,
											color: "var(--primaryTextColor)",
										}),
									}}
								/>
							</div>
						</div>
						<p>{descriptors.organizer}</p>
					</div>
				)}

				<div className={styles.formListItem}>
					<div className={styles.selectorRow}>
						<label>Invitees are</label>
						<div className={styles.inputs}>
							<Selector
								condensed
								onChange={updateFilterAttendeeType}
								options={filterAttendeeTypeOptions}
								value={filterAttendeeTypeOptions.find(option => option.value === context.rule.filterAttendeeType)}
								styles={{
									singleValue: (base: CSSObjectWithLabel) => {
										return {
											...base,
											fontWeight: 400,
											color: "var(--primaryTextColor)",
										};
									},
								}}
							/>
						</div>
					</div>
					<p>{descriptors.attendeeType}</p>
				</div>

				<NumberOfAttendeesInputs descriptors={descriptors} />

				<div className={styles.formListItem}>
					<div className={styles.formListItemColumn}>
						<div className={styles.heading}>
							<label>Contains the following keywords, emails or domains</label>
							<p>{containsSubtext}</p>
							<KeywordsInput
								placeholder="Eg. Townhall, name@email.com, @domain.com"
								setKeywords={context.setKeywordsToContain}
								keywords={context.rule.filterKeywordsContains}
							/>
						</div>
					</div>
				</div>
				<div className={styles.formListItem}>
					<div className={styles.formListItemColumn}>
						<div className={styles.heading}>
							<label>Does not contain the following keywords or domains</label>
							<p>{doesNotContainSubtext}</p>
							<KeywordsInput
								placeholder="Eg. Coffee chat, name@email.com, @domain.com"
								setKeywords={context.setKeywordsToRestrict}
								keywords={context.rule.filterKeywordsRestrict}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
